import * as React from 'react';
import { useQuery } from 'convex/react';
import { api } from '@/convex';
import type { Id } from '@/convex';
import { isActiveScanRunStatus } from '../../../convex/scanRunsShared';
import styles from './last-scan-affordance.module.scss';

export interface LastScanAffordanceProps {
  projectId: Id<'projects'> | undefined;
  repoFullName?: string;
  onRescan?: () => void;
  rescanLabel?: string;
  /** When true, the rescan button is hidden — used in read-only contexts. */
  readOnly?: boolean;
}

/**
 * Compact "Last scan: 2m ago — Rescan" affordance for the findings page
 * header. Reads the same scanRuns.getLatestForProject query as the
 * banner so the two stay in sync. While a scan is in flight the button
 * is disabled — the banner is the loud channel for in-flight state.
 */
export function LastScanAffordance({
  projectId,
  repoFullName,
  onRescan,
  rescanLabel = 'Rescan',
  readOnly = false,
}: LastScanAffordanceProps) {
  const run = useQuery(
    api.scanRuns.getLatestForProject,
    projectId ? { projectId, repoFullName, profile: 'full' } : 'skip',
  );

  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const isActive = run ? isActiveScanRunStatus(run.status) : false;
  const finishedAt = run?.finishedAt;
  const label = describeLastScan({
    status: run?.status,
    triggeredBy: run?.triggeredBy,
    finishedAt,
    now,
  });

  if (!run && !onRescan) return null;

  return (
    <div className={styles.root}>
      <span className={styles.label}>{label}</span>
      {!readOnly && onRescan ? (
        <button
          type="button"
          className={styles.rescan}
          onClick={onRescan}
          disabled={isActive}
          title={
            isActive
              ? 'A scan is already running for this project.'
              : 'Trigger a fresh scan of this repository.'
          }
        >
          {isActive ? 'Scanning...' : rescanLabel}
        </button>
      ) : null}
    </div>
  );
}

function describeLastScan(args: {
  status: string | undefined;
  triggeredBy: string | undefined;
  finishedAt: number | undefined;
  now: number;
}): string {
  if (args.status === 'queued' || args.status === 'running') {
    if (args.triggeredBy === 'onboarding-followup') {
      return 'Background scan finishing';
    }
    return 'Scan in progress';
  }
  if (args.status === 'deferred') {
    return 'Scan paused by rate limit';
  }
  if (args.status === 'failed') {
    return 'Last scan failed';
  }
  if (args.status === 'succeeded' && args.finishedAt) {
    return `Last scan ${formatAgo(args.now - args.finishedAt)}`;
  }
  return 'No scans yet';
}

function formatAgo(deltaMs: number): string {
  if (deltaMs < 60_000) return 'just now';
  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
